import type { ConsumerPolicyStatus } from '@shared/consumerPlan';
import { CareEnvironment, CarePhase } from '@shared';
import type { CareAmount, ConsumerOptionLabel, FamilyHelp } from '@shared/consumerPlan';

export const CARE_SETTING_COPY: Record<ConsumerCareSetting, { title: string; description: string }> = {
  mixed: {
    title: 'Start at home, move if needed',
    description:
      'Early care at home, assisted living as needs grow, then a nursing facility for full care.',
  },
  home: {
    title: 'Stay at home throughout',
    description: 'Professional help at home in every stage, including full care.',
  },
  assisted: {
    title: 'Assisted living',
    description: 'Start at home, then assisted living once you need regular daily help.',
  },
  nursing: {
    title: 'A nursing facility',
    description: 'Home for as long as possible, then a nursing facility for full-time care.',
  },
};

export const CARE_ENVIRONMENT_LABELS: Record<CareEnvironment, string> = {
  [CareEnvironment.home]: 'At home',
  [CareEnvironment.independentLiving]: 'Independent living',
  [CareEnvironment.assistedLiving]: 'Assisted living',
  [CareEnvironment.fullCareFacility]: 'Nursing facility',
};

// Everyday names for the usual order people lose activities of daily living. These are typical
// for each phase, not a prediction for this person: the model predicts hours and duration.
export const CARE_PHASE_COPY: Record<CarePhase, { title: string; activities: string; reason: string }> = {
  [CarePhase.earlyCare]: {
    title: 'Starting out',
    activities: 'Bathing, then dressing',
    reason: 'Balance, strength, flexibility, and dexterity usually go first.',
  },
  [CarePhase.moderateCare]: {
    title: 'As needs grow',
    activities: 'Using the toilet, then moving between bed and chair',
    reason: 'Getting to the toilet alone and shifting position both get harder.',
  },
  [CarePhase.fullCare]: {
    title: 'More hands-on support',
    activities: 'Bladder or bowel control, then eating',
    reason: 'These usually come later, when someone needs help with almost everything.',
  },
};

export const CARE_AMOUNT_COPY: Record<CareAmount, string> = {
  half: 'Half as much',
  estimate: 'Use our estimate',
  double: 'Twice as much',
};

export const FAMILY_HELP_COPY: Record<FamilyHelp, string> = {
  none: 'No family help',
  estimate: 'Use our estimate',
  all: 'Family covers the at-home care',
};

export const OPTION_LABELS: Record<ConsumerOptionLabel, string> = {
  lowerPremium: 'Lower premium',
  bestValue: 'Middle option',
  moreCoverage: 'More coverage',
};

export const COVERAGE_LANE_COPY: Record<ConsumerPaymentSchedule, { title: string; description: string }> = {
  ongoing: {
    title: 'Lower monthly cost',
    description: 'Pay a smaller amount every month for as long as you keep the policy.',
  },
  tenPay: {
    title: 'Balanced',
    description: 'Pay annually for 10 years, then your coverage is fully paid.',
  },
  singlePay: {
    title: 'Maximum protection',
    description: 'Pay once and get the most coverage for every premium dollar.',
  },
};

// None of these may suggest coverage is in force: a selection and a sent application are not policies.
export const CONSUMER_POLICY_STATUS_LABEL: Record<ConsumerPolicyStatus, string> = {
  'existing-policy': 'Existing policy',
  'added-quote': 'Selected policy',
  'added-quote-not-applied': 'Selected — not applied for',
  'application-submitted': 'Application submitted',
};

export const GUARANTEE_CONDITIONS = 'Guaranteed amounts assume the policy is issued as quoted and premiums are paid as scheduled.';

// The onboarding step the guided flow follows on from, shown first in its progress bar.
export const REVIEW_RESULTS_STEP_NAME = 'Review Results';
